import React from "react";
import { ImproveAreaDisplayInfo } from "../../../../Types/AreaDisplayInfo/AreaDisplayInfo";  
import { ISelectOption } from "../../../Utils/MultiSelect/MultiSelect";
import ImproveCardCollection from "./ImproveCardCollection";  
import LoadingWrapper from "../../Loading/LoadingWrapper";
import NoItems from "../../NoItems/NoItems";
import styles from "./PagedImproveCardCollection.module.css";

const roleToCode = (role?: string) => {
  switch ((role || "").trim().toLowerCase()) {
    case "participant": return "P";
    case "reader": return "R";
    case "owner": return "O";
    case "lead": return "L";
    case "moderator": return "M";
    case "approver": return "A";
    default: return null;
  }
}

// Map impact filter values to segment labels in impactSegments
const impactToSegmentLabel = (impact: string): string => {
  switch (impact) {
    case 'Not Met': return 'Overdue';
    case 'Partially Met': return 'In progress';
    case 'Met': return 'Completed';
    case 'Exceeded': return 'Exceeded';
    default: return '';
  }
};

const computeDisabledIds = (
  areas: ImproveAreaDisplayInfo[],
  selectedRoles: ISelectOption[] = [],
  selectedPartners: ISelectOption[] = [],
  selectedDepartments: ISelectOption[] = [],
  selectedGroups: ISelectOption[] = [],
  selectedStatus: ISelectOption[] = [],
  selectedImpact: ISelectOption[] = []
): string[] => {
  const selectedRoleCodes = selectedRoles.map(role => roleToCode(role.value)).filter((code): code is "P" | "R" | "O" | "L" | "M" | "A" => code !== null);
  const selectedPartnerNames = new Set(selectedPartners.map(p => p.label));
  const selectedDepartmentNames = new Set(selectedDepartments.map(d => d.label));
  const selectedGroupNames = new Set(selectedGroups.map(g => g.label));
  const selectedStatusValues = new Set(selectedStatus.map(s => s.value));
  const selectedImpactSegmentLabels = new Set(selectedImpact.map(i => impactToSegmentLabel(i.value)));

  const matchesAll = (area: ImproveAreaDisplayInfo) => {
    // Roles - area must match ALL selected roles
    if (selectedRoleCodes.length > 0) {
      const areaRoles = area.roles || [];
      if (!selectedRoleCodes.every(code => areaRoles.includes(code))) return false;
    }
    // Partners (Organisations)
    if (selectedPartnerNames.size > 0) {
      const orgs = area.organisations || [];
      if (!orgs.some(o => selectedPartnerNames.has(o))) return false;
    }
    // Departments
    if (selectedDepartmentNames.size > 0) {
      const deps = area.departments || [];
      if (!deps.some(d => selectedDepartmentNames.has(d))) return false;
    }
    // Groups
    if (selectedGroupNames.size > 0) {
      const groups = area.groups || [];
      if (!groups.some(g => selectedGroupNames.has(g))) return false;
    }
    // Status - case-insensitive comparison to handle backend variations
    if (selectedStatusValues.size > 0) {
      const areaStatusLower = area.status?.toLowerCase() || '';
      const matchesStatus = Array.from(selectedStatusValues).some(selectedStatus => 
        selectedStatus.toLowerCase() === areaStatusLower
      );
      if (!matchesStatus) return false;
    }
    // Impact - check if area has non-zero value for any selected impact segment
    if (selectedImpactSegmentLabels.size > 0) {
      const impactSegments = area.impactSegments || [];
      const hasMatchingImpact = Array.from(selectedImpactSegmentLabels).some(segmentLabel => {
        const segment = impactSegments.find(s => s.label === segmentLabel);
        return segment && segment.value > 0;
      });
      if (!hasMatchingImpact) return false;
    }
    return true;
  };

  return areas.filter(a => !matchesAll(a)).map(a => a.id);
}

// New function to reorder areas: matching first, then filtered out
const reorderAreas = (
  areas: ImproveAreaDisplayInfo[],
  disabledIds: string[]
): ImproveAreaDisplayInfo[] => {
  const matchingAreas = areas.filter(area => !disabledIds.includes(area.id));
  const filteredOutAreas = areas.filter(area => disabledIds.includes(area.id));
  
  return [...matchingAreas, ...filteredOutAreas];
};
  
type PagedImproveCardCollectionProps = {  
  archived: boolean;  
  displayInfos: ImproveAreaDisplayInfo[];
  toggleArchiveHandler: (id: string) => void;
  loading: boolean;
  selectedRoleOption?: ISelectOption[];
  selectedPartners?: ISelectOption[];
  selectedDepartments?: ISelectOption[];
  selectedGroups?: ISelectOption[];
  selectedStatus?: ISelectOption[];
  selectedImpact?: ISelectOption[];
  searchTerm?: string;
  listHeader?: React.ReactNode;
  hideExportButton?: boolean;
  onCardExport?: (id: string) => void;
};  
  
const PagedImproveCardCollection = ({  
  archived,  
  displayInfos = [],   
  toggleArchiveHandler,
  loading,  
  selectedRoleOption,
  selectedPartners = [],
  selectedDepartments = [],
  selectedGroups = [],
  selectedStatus = [],
  selectedImpact = [],
  searchTerm = "",
  listHeader,
  hideExportButton,
  onCardExport,
}: PagedImproveCardCollectionProps) => {

  // Compute disabled IDs and reorder areas
  const disabledIds = computeDisabledIds(displayInfos, selectedRoleOption, selectedPartners, selectedDepartments, selectedGroups, selectedStatus, selectedImpact);
  const reorderedAreas = reorderAreas(displayInfos, disabledIds);
  
  return (  
    <LoadingWrapper loading={loading}>
      <div className={styles.outerContainer}>  
        {listHeader ? (
          <div className={styles.listHeaderSlot}>{listHeader}</div>
        ) : null}
        {reorderedAreas.length === 0 ? (  
          <NoItems/>
        ) : (  
          <>  
            <ImproveCardCollection 
              toggleArchiveHandler={toggleArchiveHandler}
              archived={archived} 
              areas={reorderedAreas.filter(a => !searchTerm || a.title.toLowerCase().includes(searchTerm.toLowerCase()))} 
              disabledIds={disabledIds}
              hideExportButton={hideExportButton}
              onCardExport={onCardExport}
            />  
          </>  
        )}  
      </div>  
    </LoadingWrapper>
  );  
 
};  
  
export default PagedImproveCardCollection;

